import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Person,
  Event,
  Info,
  CheckCircle,
  Close,
} from '@mui/icons-material';
import { useNotifications } from '@hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { NOTIFICATION_TYPES } from '@services/notificationService';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead, dismiss } = useNotifications();

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        await markAsRead(notification.notificationId);
      }
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.VOLUNTEER_APPROVED:
      case NOTIFICATION_TYPES.VOLUNTEER_PENDING:
      case NOTIFICATION_TYPES.VOLUNTEER_REJECTED:
        return <Person color="primary" />;
      case NOTIFICATION_TYPES.EVENT_CREATED:
      case NOTIFICATION_TYPES.EVENT_UPDATED:
      case NOTIFICATION_TYPES.EVENT_CANCELLED:
      case NOTIFICATION_TYPES.ASSIGNMENT_CONFIRMED:
      case NOTIFICATION_TYPES.ASSIGNMENT_CANCELLED:
        return <Event color="secondary" />;
      default:
        return <Info color="action" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} unread`} color="error" size="small" />
          )}
        </Box>
        {unreadCount > 0 && (
          <Button
            variant="outlined"
            onClick={handleMarkAllAsRead}
            startIcon={<CheckCircle />}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {!error && notifications.length === 0 && (
        <Paper sx={{ textAlign: 'center', py: 8 }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notifications yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll see updates about events, approvals, and more here.
          </Typography>
        </Paper>
      )}

      {/* Notification list */}
      {!error && notifications.length > 0 && (
        <Paper>
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <Box key={notification.notificationId}>
                <ListItem
                  disablePadding
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  }}
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ py: 2, pr: 1 }}
                  >
                    <ListItemIcon>{getNotificationIcon(notification.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          fontWeight={notification.read ? 'regular' : 'bold'}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(notification.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'error.main',
                          ml: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </ListItemButton>
                  <Tooltip title="Dismiss">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); dismiss(notification.notificationId); }}
                      sx={{ mr: 1, flexShrink: 0 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
};

export default NotificationsPage;
